import { useCallback, useState } from 'react';
import {
  HsmCampaignMessage,
  HsmCampaignMessageRequest
} from '../types';
import * as hsmService from '../services/hsm.service';
import { withAuthRetry } from '../utils/withAuthRetry';

export interface UseHsmMessagesProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
  canManageHsm: boolean;
}

export interface UseHsmMessagesReturn {
  // State
  campaignMessages: HsmCampaignMessage[];
  messagesLoading: boolean;
  selectedMessage: HsmCampaignMessage | null;
  messageDialogOpen: boolean;
  setMessageDialogOpen: (open: boolean) => void;

  // Form state
  messageDay: string;
  setMessageDay: (value: string) => void;
  messageTemplateName: string;
  setMessageTemplateName: (value: string) => void;
  messageLanguageCode: string;
  setMessageLanguageCode: (value: string) => void;
  messageTemplateParameters: string;
  setMessageTemplateParameters: (value: string) => void;
  messageVariableMapping: string[];
  setMessageVariableMapping: (value: string[]) => void;
  messageSaving: boolean;

  // Actions
  fetchCampaignMessages: () => Promise<{ success: boolean; error?: string }>;
  createCampaignMessage: (data: HsmCampaignMessageRequest) => Promise<{ success: boolean; error?: string }>;
  updateCampaignMessage: (messageId: number, data: Partial<HsmCampaignMessageRequest>) => Promise<{ success: boolean; error?: string }>;
  deleteCampaignMessage: (messageId: number) => Promise<{ success: boolean; error?: string }>;
  openNewMessageDialog: () => void;
  openEditMessageDialog: (message: HsmCampaignMessage) => void;
  resetMessageForm: () => void;
  loadMessageToForm: (message: HsmCampaignMessage) => void;
}

export function useHsmMessages({
  accessToken,
  refreshToken,
  attemptRefresh,
  canManageHsm
}: UseHsmMessagesProps): UseHsmMessagesReturn {
  // State
  const [campaignMessages, setCampaignMessages] = useState<HsmCampaignMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<HsmCampaignMessage | null>(null);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);

  // Form state
  const [messageDay, setMessageDay] = useState('1');
  const [messageTemplateName, setMessageTemplateName] = useState('');
  const [messageLanguageCode, setMessageLanguageCode] = useState('pt_BR');
  const [messageTemplateParameters, setMessageTemplateParameters] = useState('');
  const [messageVariableMapping, setMessageVariableMapping] = useState<string[]>([]);
  const [messageSaving, setMessageSaving] = useState(false);

  // API calls
  const fetchCampaignMessages = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken || !canManageHsm) return { success: false, error: 'Nao autorizado' };

    setMessagesLoading(true);

    try {
      const data = await withAuthRetry(
        () => hsmService.fetchCampaignMessages(accessToken!),
        refreshToken,
        attemptRefresh
      );
      setCampaignMessages(Array.isArray(data) ? data : []);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao buscar mensagens'
      };
    } finally {
      setMessagesLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, canManageHsm]);

  const createCampaignMessage = useCallback(
    async (data: HsmCampaignMessageRequest): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return { success: false, error: 'Nao autenticado' };

      setMessageSaving(true);

      try {
        await withAuthRetry(
          () => hsmService.createCampaignMessage(data, accessToken!),
          refreshToken,
          attemptRefresh
        );
        await fetchCampaignMessages();
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao criar mensagem'
        };
      } finally {
        setMessageSaving(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh, fetchCampaignMessages]
  );

  const updateCampaignMessage = useCallback(
    async (messageId: number, data: Partial<HsmCampaignMessageRequest>): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return { success: false, error: 'Nao autenticado' };

      setMessageSaving(true);

      try {
        await withAuthRetry(
          () => hsmService.updateCampaignMessage(messageId, data, accessToken!),
          refreshToken,
          attemptRefresh
        );
        await fetchCampaignMessages();
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao atualizar mensagem'
        };
      } finally {
        setMessageSaving(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh, fetchCampaignMessages]
  );

  const deleteCampaignMessage = useCallback(
    async (messageId: number): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return { success: false, error: 'Nao autenticado' };

      try {
        await withAuthRetry(
          () => hsmService.deleteCampaignMessage(messageId, accessToken!),
          refreshToken,
          attemptRefresh
        );
        await fetchCampaignMessages();
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao excluir mensagem'
        };
      }
    },
    [accessToken, refreshToken, attemptRefresh, fetchCampaignMessages]
  );

  // Form helpers
  const resetMessageForm = useCallback(() => {
    setMessageDay('1');
    setMessageTemplateName('');
    setMessageLanguageCode('pt_BR');
    setMessageTemplateParameters('');
    setMessageVariableMapping([]);
    setSelectedMessage(null);
  }, []);

  const loadMessageToForm = useCallback((message: HsmCampaignMessage) => {
    setMessageDay(String(message.day));
    setMessageTemplateName(message.templateName);
    setMessageLanguageCode(message.languageCode);
    setMessageTemplateParameters(message.templateParameters || '');
    setMessageVariableMapping(message.variableMapping || []);
    setSelectedMessage(message);
  }, []);

  // Dialog handlers
  const openNewMessageDialog = useCallback(() => {
    resetMessageForm();
    setMessageDialogOpen(true);
  }, [resetMessageForm]);

  const openEditMessageDialog = useCallback(
    (message: HsmCampaignMessage) => {
      loadMessageToForm(message);
      setMessageDialogOpen(true);
    },
    [loadMessageToForm]
  );

  return {
    // State
    campaignMessages,
    messagesLoading,
    selectedMessage,
    messageDialogOpen,
    setMessageDialogOpen,

    // Form state
    messageDay,
    setMessageDay,
    messageTemplateName,
    setMessageTemplateName,
    messageLanguageCode,
    setMessageLanguageCode,
    messageTemplateParameters,
    setMessageTemplateParameters,
    messageVariableMapping,
    setMessageVariableMapping,
    messageSaving,

    // Actions
    fetchCampaignMessages,
    createCampaignMessage,
    updateCampaignMessage,
    deleteCampaignMessage,
    openNewMessageDialog,
    openEditMessageDialog,
    resetMessageForm,
    loadMessageToForm
  };
}
